import './index.css'
import cn from 'clsx'

import React, { useCallback, useEffect, useState } from 'react'
import CopyToClipboard from 'react-clipboard.js'
import { useDispatch } from 'hooks/use-redux'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { pushUINotification } from '@disco/utils/notifications'
import Row from 'components/common/row'
import DialogFooter from 'components/modals/dialog-footer'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import Checkbox from 'components/ui/checkbox'
import Flex from 'components/ui/flex'
import Label from 'components/ui/label'
import TextField from 'components/ui/text-field'
import ColorPicker from './colorPicker'
import CopyUrlItem from 'components/ui/copy-url-item'
import SizePicker from './sizePicker'
import { Box } from 'components/ds'

const THEMES = [
  {
    id: 'white',
    name: 'Light',
    color: '#fff',
  },
  {
    id: 'dark',
    name: 'Dark',
    color: '#333',
  },
]
const CONTROLS = [
  {
    id: '#4E98FF',
    color: '#4E98FF',
    name: 'Blue',
  },
  {
    id: '#3D5B01',
    color: '#3D5B01',
    name: 'Green',
  },
  {
    id: '#910460',
    color: '#910460',
    name: 'Magenta',
  },
  {
    id: '#D095FF',
    color: '#D095FF',
    name: 'Purple',
  },
]
const DEFAULT_PLAYLIST_HEIGHT = 395
const DEFAULT_TRACK_HEIGHT = 235
const DEFAULT_WIDTH = 480
const MIN_PLAYLIST_HEIGHT = 285
const MIN_WIDTH = 355
type Params = {
  artwork: boolean
  color: string
  theme: string
}

const getFrameSrc = (url: string, params: Params): string => {
  if (url) {
    const src = new URL(url)
    Object.keys(params).forEach((key: string) =>
      src.searchParams.append(key, String(params[key])),
    )
    return src.toString()
  }

  return url
}

type Props = {
  disablePublicDownloads: boolean
  embedParams: {
    download: boolean
  }
  embedUrl: string
  iframeId: string
  type: 'playlist' | 'track'
  watermark?: boolean | null | undefined
  onClose: (...args: Array<any>) => any
  onParamsChange: (...args: Array<any>) => any
}

const isValidHexColor = (hex: string): boolean => {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex)
}

const EmbedTab = ({
  disablePublicDownloads,
  embedParams,
  embedUrl,
  iframeId,
  type,
  watermark,
  onClose,
  onParamsChange,
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  useEffect(() => {
    const mixpanelEvent =
      type === 'playlist'
        ? 'Open embed tab (playlists)'
        : 'Open embed tab (tracks)'
    dispatch(sendMixpanelEvent(mixpanelEvent))
  }, [])
  const [params, setParams] = useState<Params>({
    artwork: true,
    color: '#4E98FF',
    theme: 'white',
  })
  const [customColor, setCustomColor] = useState('#4E98FF')
  const [persistedCustomColor, setPersistedCustomColor] = useState('#FFFC52')
  const [isCustomColorActive, setCustomColorActive] = useState(false)
  const [size, setSize] = useState<{
    width: number
    height: number
  }>({
    width: DEFAULT_WIDTH,
    height: type === 'track' ? DEFAULT_TRACK_HEIGHT : DEFAULT_PLAYLIST_HEIGHT,
  })
  const [isCustomizeOpen, setCustomizeOpen] = useState<boolean>(false)
  const toggleCustomizeOpen = useCallback(() => {
    dispatch(sendMixpanelEvent('Embed tab - Customize player'))
    setCustomizeOpen(isCustomizeOpen => !isCustomizeOpen)
  }, [])
  const handleCopy = useCallback(() => {
    const mixpanelEvent =
      type === 'playlist'
        ? 'Embed tab - Copy embed code (playlists)'
        : 'Embed tab - Copy embed code (tracks)'
    dispatch(sendMixpanelEvent(mixpanelEvent))
    pushUINotification({
      level: 'success',
      message: 'Embed code copied!',
    })
  }, [dispatch, type])

  const handleControlColorChange = (color: string) => {
    if (color === 'custom') {
      setCustomColorActive(true)
      setCustomColor(persistedCustomColor)
      setParams(state => ({ ...state, color: persistedCustomColor }))
    } else {
      setCustomColorActive(false)
      setCustomColor(color)
      setParams(state => ({ ...state, color }))
    }
  }

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setCustomColor(newValue)
    if (isCustomColorActive) {
      if (isValidHexColor(newValue)) {
        setParams(state => ({ ...state, color: newValue }))
        setPersistedCustomColor(newValue)
      }
    }
  }

  const minHeight =
    type === 'track' ? DEFAULT_TRACK_HEIGHT : MIN_PLAYLIST_HEIGHT
  const frameWidth = size.width >= MIN_WIDTH ? size.width : MIN_WIDTH
  const frameHeight = size.height >= minHeight ? size.height : minHeight
  const frameSrc = getFrameSrc(embedUrl, params)
  const frameCode = `<iframe id="${iframeId}" name="${iframeId}" allowfullscreen frameborder="0" class="disco-embed" src="${frameSrc}" width="${frameWidth}" height="${frameHeight}"></iframe>`

  const renderCustomize = () => (
    <>
      <div className="EmbedTab-customize">
        <div className="EmbedTab-customize-item">
          <Label
            caption="Player Size"
            tooltipContent={`Player must be at least ${MIN_WIDTH} px wide and ${minHeight} px high`}
            tooltipPosition="top"
          />
          <SizePicker
            minWidth={MIN_WIDTH}
            minHeight={minHeight}
            readonlyHeight={type === 'track'}
            value={size}
            onChange={setSize}
          />
        </div>

        <div className="EmbedTab-customize-item">
          <Label caption="Color Theme" />
          <ColorPicker
            items={THEMES}
            value={params.theme}
            onChange={theme => {
              setParams(state => ({ ...state, theme }))
            }}
          />
        </div>

        <div className="EmbedTab-customize-item">
          <Label caption="Control Colors" />
          <ColorPicker
            items={[
              ...CONTROLS,
              {
                id: 'custom',
                name: 'Custom',
                color: isValidHexColor(persistedCustomColor)
                  ? persistedCustomColor
                  : '#ffffff',
              },
            ]}
            value={isCustomColorActive ? 'custom' : params.color}
            onChange={handleControlColorChange}
          />
          {isCustomColorActive && (
            <Box mt="3">
              <TextField
                value={customColor}
                onChange={handleCustomColorChange}
                placeholder="#000000"
              />
            </Box>
          )}
        </div>
      </div>
      <Flex spacing="large">
        <Checkbox
          checked={embedParams.download}
          label="Enable downloads"
          disabled={disablePublicDownloads}
          onChange={() =>
            onParamsChange({
              download: !embedParams.download,
            })
          }
        />
        <Checkbox
          checked={params.artwork}
          label="Enable artwork"
          onChange={() => {
            setParams(state => ({ ...state, artwork: !state.artwork }))
          }}
        />
      </Flex>
    </>
  )

  return (
    <>
      {watermark && (
        <Row columnsSize={[12]} marginBottom="small">
          <div>
            <Alert icon="warning" status="warning">
              Watermarked tracks downloaded from embedded players will not be
              traceable to individual recipients.
            </Alert>
          </div>
        </Row>
      )}
      <>
        <Row columnsSize={[12]}>
          <div>
            <CopyUrlItem
              label="Embed code"
              url={frameCode}
              onCopy={handleCopy}
              hideNewWindow={true}
            />
          </div>
        </Row>
        <div className="EmbedTab-preview">
          <Row columnsSize={[12]}>
            <div>
              <Label caption="Player preview" />
              <iframe
                className={cn('EmbedTab-iframe', {
                  'EmbedTab-iframe--compact': type === 'track',
                })}
                src={frameSrc}
              />
            </div>
          </Row>
          <DialogFooter
            corner={
              <Button
                appearance="inline-link"
                caption={!isCustomizeOpen ? 'Customize player' : 'Hide options'}
                onClick={toggleCustomizeOpen}
              />
            }
            primary={
              <CopyToClipboard
                component="div"
                data-clipboard-text={frameCode}
                onClick={handleCopy}
              >
                <Button appearance="primary" caption="Copy embed code" />
              </CopyToClipboard>
            }
            onClose={onClose}
          />
          {isCustomizeOpen && renderCustomize()}
        </div>
      </>
    </>
  )
}

export default EmbedTab
